import { Gesture, GestureDetector } from "@-/com.npmjs.react-native-gesture-handler";
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import { Platform, View, useWindowDimensions } from "@-/com.npmjs.react-native";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { useNavAccountDrawer } from "@-/dev.improve.haniverse.routes.nav._components.account-drawer";
import { usePrimaryControlsStripBackResolution } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.back";
import { usePrimaryControlsStripToolbarHeight } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.toolbar";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import { useScopedBack } from "@-/dev.improve.haniverse.routes.nav._lib.back";
import { subscribeToAndroidEdgeBack } from "@-/dev.improve.haniverse.modules.edge-navigation";
import useLatest from "@-/com.npmjs.use-latest";
import type { Tagged } from "@-/com.npmjs.type-fest";
import { createGate } from "@-/dev.improve.haniverse.lib.gate";
import { useCallback, useEffect, useMemo } from "react";

/** The tallest header row a screen can draw without publishing a toolbar height (auth: 60pt). */
const APP_BAR_MIN_HEIGHT = 64;

type LeftEdgeBackSwipeGate = Tagged<{ back: () => void }, "LeftEdgeBackSwipeGate">;

/** The scoped back action: pop within the focused tab, else navigate to the published fallback. */
function useEdgeBack(): { back: () => void; backAvailable: boolean } {
  const { canGoBack, goBack } = useScopedBack();
  const { fallbackHref } = usePrimaryControlsStripBackResolution();
  const back = useCallback(() => goBack({ fallbackHref }), [fallbackHref, goBack]);
  return { back, backAvailable: canGoBack || fallbackHref !== undefined };
}

/** Routes Android's system edge-back gestures: left pops back (or closes the drawer), right opens it. */
function useAndroidEdgeBack({ back, enabled }: { back: () => void; enabled: boolean }) {
  const { closeDrawer, isOpen, openDrawer } = useNavAccountDrawer();
  const handleSystemBack = useLatest((edge: "left" | "right") => {
    if (edge === "right") {
      openDrawer();
    } else if (isOpen) {
      closeDrawer();
    } else {
      back();
    }
  });
  useEffect(() => {
    if (!enabled) {
      return;
    }
    return subscribeToAndroidEdgeBack((edge) => handleSystemBack.current(edge));
  }, [enabled, handleSystemBack]);
}

/**
 * Whether the in-app left-edge swipe strip shows: Android (iOS uses the native stack's
 * interactive pop), edge navigation on, the drawer closed, and somewhere to go back to. `null` is
 * the designed hidden state.
 */
function getLeftEdgeBackSwipeGate({
  back,
  backAvailable,
  enabled,
  isDrawerOpen,
}: {
  back: () => void;
  backAvailable: boolean;
  enabled: boolean;
  isDrawerOpen: boolean;
}): LeftEdgeBackSwipeGate | null {
  return enabled && !isDrawerOpen && backAvailable && Platform.OS !== "ios"
    ? createGate<LeftEdgeBackSwipeGate>({ back })
    : null;
}

/**
 * Android left-edge back; iOS uses the native stack interactive pop. The drawer owns the right edge.
 * Mounted inside the account drawer (it reads the drawer state) for as long as the nav shell is: it
 * owns the system edge-back subscription, and decides whether the swipe strip draws.
 */
export function NavEdgeGestures() {
  const [enabled] = useDeviceSetting("isEdgeNavigationEnabled");
  const { isOpen } = useNavAccountDrawer();
  const { back, backAvailable } = useEdgeBack();
  useAndroidEdgeBack({ back, enabled });
  const leftEdgeBackSwipeGate = getLeftEdgeBackSwipeGate({
    back,
    backAvailable,
    enabled,
    isDrawerOpen: isOpen,
  });
  return (
    <>{leftEdgeBackSwipeGate !== null && <LeftEdgeBackSwipe gate={leftEdgeBackSwipeGate} />}</>
  );
}

function LeftEdgeBackSwipe({ gate: { back: onComplete } }: { gate: LeftEdgeBackSwipeGate }) {
  const { height } = useWindowDimensions();
  // Start the strip BELOW the header. The header's back control sits inside the left 24pt, and a
  // pan-only strip over it swallows the tap without ever activating, so "Back" did nothing
  // (staging run 35931410546: every Android flow that taps "Back" stayed on its page). Screens with
  // their own header (auth: a 44pt back button in an 8pt-padded row) publish no toolbar height, so
  // the app-bar band is always kept clear.
  const insets = useSafeAreaInsets();
  const headerClearance = Math.max(usePrimaryControlsStripToolbarHeight(), APP_BAR_MIN_HEIGHT);
  const distance = useSharedValue(0);
  const touchY = useSharedValue(0);
  const visible = useSharedValue(0);
  const gesture = useMemo(
    () =>
      Gesture.Pan()
        .maxPointers(1)
        .activeOffsetX(10)
        .failOffsetY([-18, 18])
        .onStart((event) => {
          // Window-relative: the strip starts below the header, the indicator's container does not.
          touchY.set(Math.max(48, Math.min(height - 96, event.absoluteY)));
          distance.set(0);
          visible.set(withTiming(1, { duration: 80 }));
        })
        .onUpdate((event) => {
          distance.set(Math.max(0, event.translationX));
        })
        .onEnd((event, success) => {
          if (!success) {
            return;
          }
          const inward = event.translationX;
          const velocity = event.velocityX;
          // A short flick is deliberate; reversing or cancelling never navigates.
          if (velocity >= -50 && (inward >= 72 || (inward >= 28 && velocity >= 600))) {
            runOnJS(onComplete)();
          }
        })
        .onFinalize(() => {
          visible.set(withTiming(0, { duration: 120 }));
          distance.set(withTiming(0, { duration: 160 }));
        }),
    [distance, height, onComplete, touchY, visible],
  );
  const indicatorStyle = useAnimatedStyle(() => ({
    opacity: visible.get(),
    top: touchY.get() - 24,
    transform: [
      { translateX: Math.min(distance.get() * 0.6, 64) },
      { scale: 0.8 + Math.min(distance.get() / 72, 1) * 0.2 },
    ],
  }));

  return (
    <View className="absolute inset-0 z-50" pointerEvents="box-none">
      <GestureDetector gesture={gesture}>
        <View
          className="absolute bottom-0 left-0 w-6"
          collapsable={false}
          style={{ top: insets.top + headerClearance }}
          testID="nav-left-edge-gesture"
        />
      </GestureDetector>
      <Animated.View
        className="absolute -left-10 size-12 items-center justify-center rounded-full border border-border bg-surface shadow-lg"
        pointerEvents="none"
        style={indicatorStyle}
        testID="nav-edge-back-indicator"
      >
        <Icon className="text-foreground" name="FiChevronLeft" size={24} />
      </Animated.View>
    </View>
  );
}
